import React, { useState } from 'react';
import { usePageMeta } from '../hooks/usePageMeta';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Code, Lock, Mail, User, Building2, Briefcase, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DeveloperAuth = () => {
  usePageMeta('Accesso Sviluppatori | SpedireOnline Developer Portal', 'Accedi o registrati al portale sviluppatori SpedireOnline per ottenere le chiavi API e integrare i servizi di spedizione, tracking e ritiro nella tua piattaforma.');
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [loginForm, setLoginForm] = useState({
    email: '',
    password: ''
  });
  
  const [registerForm, setRegisterForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    role: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/developers/login`, loginForm);
      if (response.data.access_token) {
        localStorage.setItem('dev_token', response.data.access_token);
        localStorage.setItem('dev_email', loginForm.email);
        toast.success('Login effettuato con successo!');
        navigate('/developer-dashboard');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Errore durante il login');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error('Le password non coincidono');
      return;
    }
    
    if (!registerForm.acceptTerms) {
      toast.error('Devi accettare i Termini e la Privacy Policy');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/developers/register`, {
        first_name: registerForm.firstName,
        last_name: registerForm.lastName,
        email: registerForm.email,
        company: registerForm.company,
        role: registerForm.role,
        password: registerForm.password
      });
      
      if (response.data.success) {
        toast.success('Registrazione completata! Effettua il login.');
        setIsLogin(true);
        setLoginForm({ email: registerForm.email, password: '' });
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Errore durante la registrazione');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-primary)' }}>
      {/* Header */}
      <nav className="nav-bar">
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
          <img 
            src="https://customer-assets.emergentagent.com/job_ship-smart-6/artifacts/4h2rj0xf_logoonly.png" 
            alt="SpedireOnline.net" 
            className="h-8 w-8 object-contain"
          />
          <span className="font-bold text-xl" style={{ color: 'var(--brand-primary)' }}>spedireonline.net</span>
        </div>
        <Button variant="outline" onClick={() => navigate('/')}>Torna alla Home</Button>
      </nav>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-md mx-auto">
          {/* Hero Text */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ background: 'var(--accent-wash)' }}>
              <Code className="w-8 h-8" style={{ color: 'var(--accent-primary)' }} />
            </div>
            <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
              {isLogin ? 'Accesso Sviluppatori' : 'Registrati come Sviluppatore'}
            </h1>
            <p className="text-base" style={{ color: 'var(--text-secondary)' }}>
              {isLogin 
                ? 'Accedi alla tua dashboard e alle API' 
                : 'Inizia a integrare le nostre API nella tua applicazione'
              }
            </p>
          </div>

          {/* Auth Card */}
          <Card>
            <CardHeader>
              <CardTitle>{isLogin ? 'Login' : 'Crea il tuo account'}</CardTitle>
              <CardDescription>
                {isLogin 
                  ? 'Inserisci le tue credenziali per accedere' 
                  : 'Compila il form per registrarti come sviluppatore'
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLogin ? (
                // Login Form
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                      <Mail className="w-4 h-4 inline mr-2" />
                      Email
                    </label>
                    <Input
                      type="email"
                      placeholder="tua@email.com"
                      value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                      <Lock className="w-4 h-4 inline mr-2" />
                      Password
                    </label>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={loginForm.password}
                        onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? 'Accesso in corso...' : 'Accedi'}
                  </Button>
                </form>
              ) : (
                // Registration Form
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                        <User className="w-4 h-4 inline mr-2" />
                        Nome
                      </label>
                      <Input
                        type="text"
                        placeholder="Mario"
                        value={registerForm.firstName}
                        onChange={(e) => setRegisterForm({ ...registerForm, firstName: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                        Cognome
                      </label>
                      <Input
                        type="text"
                        placeholder="Rossi"
                        value={registerForm.lastName}
                        onChange={(e) => setRegisterForm({ ...registerForm, lastName: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                      <Mail className="w-4 h-4 inline mr-2" />
                      Email
                    </label>
                    <Input
                      type="email"
                      placeholder="mario.rossi@azienda.it"
                      value={registerForm.email}
                      onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                      <Building2 className="w-4 h-4 inline mr-2" />
                      Azienda
                    </label>
                    <Input
                      type="text"
                      placeholder="Nome azienda"
                      value={registerForm.company}
                      onChange={(e) => setRegisterForm({ ...registerForm, company: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                      <Briefcase className="w-4 h-4 inline mr-2" />
                      Ruolo
                    </label>
                    <Input
                      type="text"
                      placeholder="es. Sviluppatore Backend, CTO, ecc."
                      value={registerForm.role}
                      onChange={(e) => setRegisterForm({ ...registerForm, role: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                      <Lock className="w-4 h-4 inline mr-2" />
                      Password
                    </label>
                    <Input
                      type="password"
                      placeholder="••••••••"
                      value={registerForm.password}
                      onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                      required
                      minLength={8}
                    />
                    <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Minimo 8 caratteri</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                      Conferma Password
                    </label>
                    <Input
                      type="password"
                      placeholder="••••••••"
                      value={registerForm.confirmPassword}
                      onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })}
                      required
                    />
                  </div>

                  <div className="flex items-start gap-2">
                    <Checkbox
                      id="terms"
                      checked={registerForm.acceptTerms}
                      onCheckedChange={(checked) => setRegisterForm({ ...registerForm, acceptTerms: checked })}
                    />
                    <label htmlFor="terms" className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                      Accetto i{' '}
                      <a href="https://www.iubenda.com/privacy-policy/99691573" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: 'var(--accent-primary)' }}>
                        Termini di Servizio
                      </a>
                      {' '}e la{' '}
                      <a href="https://www.iubenda.com/privacy-policy/99691573" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: 'var(--accent-primary)' }}>
                        Privacy Policy
                      </a>
                    </label>
                  </div>

                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? 'Registrazione in corso...' : 'Registrati'}
                  </Button>
                </form>
              )}

              {/* Toggle Login/Register */}
              <div className="mt-6 text-center">
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  {isLogin ? 'Non hai un account?' : 'Hai già un account?'}
                  {' '}
                  <button
                    onClick={() => setIsLogin(!isLogin)}
                    className="font-semibold underline"
                    style={{ color: 'var(--accent-primary)' }}
                  >
                    {isLogin ? 'Registrati' : 'Accedi'}
                  </button>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DeveloperAuth;